import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';

// Import des sous-composants
import UtilisateurBulkActions from './utilisateur/UtilisateurBulkActions';
import UtilisateurFilters from './utilisateur/UtilisateurFilters';
import UtilisateursList from './utilisateur/UtilisateursList';
import UtilisateurForm from './utilisateur/UtilisateurForm';

const UtilisateurTab = () => {
  const navigate = useNavigate();
  
  // États pour les données
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  
  // États pour le chargement
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // États pour la recherche et filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  
  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  
  // États pour le formulaire
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    telephone: '',
    password: '',
    confirmPassword: '',
    image: null,
    is_active: true,
    is_staff: false,
    is_superuser: false,
    role: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  
  // États pour la sélection multiple
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // États pour le changement de mot de passe
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Debounce pour la recherche
  const [searchDebounce, setSearchDebounce] = useState('');

  // Effect pour le debounce de la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Effect pour charger les données
  useEffect(() => {
    fetchUsers();
  }, [currentPage, selectedStatus, selectedRole, searchDebounce]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/utilisateurs/roles/simple/');
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des rôles:', error);
      setRoles([]);
    }
  };

  const fetchUsers = async (page = currentPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: pageSize.toString()
      });
      
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedRole) params.append('role', selectedRole);
      if (searchDebounce.trim()) params.append('search', searchDebounce.trim());

      const response = await api.get(`/utilisateurs/liste-utilisateur/?${params}`);
      setUsers(response.data.results || []);
      setTotalPages(Math.ceil((response.data.count || 0) / pageSize));
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    let newValue = value;
    
    if (type === 'checkbox') {
      newValue = checked;
    } else if (type === 'file') {
      newValue = files[0];
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal
  const handleOpenModal = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        telephone: user.telephone || '',
        image: user.image || null,
        is_active: user.is_active,
        is_staff: user.is_staff,
        is_superuser: user.is_superuser,
        role: user.role || '',
        password: '',
        confirmPassword: ''
      });
    } else {
      setSelectedUser(null);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        telephone: '',
        password: '',
        confirmPassword: '',
        image: null,
        is_active: true,
        is_staff: false,
        is_superuser: false,
        role: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.email.trim()) {
      errors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "L'email est invalide";
    }
    
    if (!selectedUser) {
      if (!formData.password.trim()) {
        errors.password = "Le mot de passe est requis";
      }
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = "Les mots de passe ne correspondent pas";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== 'confirmPassword' && formData[key] !== null) {
          // Traitement spécial pour le champ role
          if (key === 'role') {
            // Si le role est vide ou undefined, ne pas l'inclure dans FormData
            if (formData[key] && formData[key] !== '') {
              data.append(key, formData[key]);
            }
          } else {
            data.append(key, formData[key]);
          }
        }
      });

      if (selectedUser) {
        await api.patch(`/utilisateurs/${selectedUser.id}/`, data);
        toast.success("Utilisateur modifié avec succès");
      } else {
        await api.post('/utilisateurs/register/', data);
        toast.success("Utilisateur créé avec succès");
      }
      
      fetchUsers();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(error.response?.data?.error || "Une erreur est survenue");
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (user) => {
    setUserToDelete(user);
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/utilisateurs/${userToDelete.id}/delete/`);
      toast.success("Utilisateur supprimé avec succès");
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error("Impossible de supprimer cet utilisateur. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  // Ouvrir le modal de changement de mot de passe
  const handleOpenPasswordModal = (user) => {
    setSelectedUserForPassword(user);
    setNewPassword('');
    setConfirmNewPassword('');
    setIsPasswordModalOpen(true);
  };

  // Changer le mot de passe
  const handlePasswordChange = async () => {
    if (newPassword !== confirmNewPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    try {
      await api.post(`/utilisateurs/${selectedUserForPassword.id}/change-password/`, {
        new_password: newPassword
      });

      toast.success("Mot de passe modifié avec succès");
      setIsPasswordModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.error || "Une erreur est survenue");
    }
  };

  // Navigation vers les permissions
  const handleNavigateToPermissions = (userId) => {
    navigate(`/permissions/${userId}`);
  };

  // Fonctions pour les actions en lot
  const handleBulkActivate = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Veuillez sélectionner au moins un utilisateur');
      return;
    }

    setBulkActionLoading(true);
    try {
      const promises = selectedUsers.map(userId => 
        api.patch(`/utilisateurs/${userId}/`, { is_active: true })
      );
      
      await Promise.all(promises);
      toast.success(`${selectedUsers.length} utilisateur(s) activé(s) avec succès`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de l\'activation en lot:', error);
      toast.error('Erreur lors de l\'activation des utilisateurs');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Veuillez sélectionner au moins un utilisateur');
      return;
    }

    setBulkActionLoading(true);
    try {
      const promises = selectedUsers.map(userId => 
        api.patch(`/utilisateurs/${userId}/`, { is_active: false })
      );
      
      await Promise.all(promises);
      toast.success(`${selectedUsers.length} utilisateur(s) désactivé(s) avec succès`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la désactivation en lot:', error);
      toast.error('Erreur lors de la désactivation des utilisateurs');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Fonction pour sélectionner/désélectionner un utilisateur
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Fonction pour sélectionner/désélectionner tous les utilisateurs
  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  return (
    <div className="space-y-6">
      {/* Actions en lot */}
      <UtilisateurBulkActions
        selectedUtilisateurs={selectedUsers}
        bulkActionLoading={bulkActionLoading}
        onBulkActivate={handleBulkActivate}
        onBulkDeactivate={handleBulkDeactivate}
        onOpenModal={handleOpenModal}
      />

      {/* Filtres */}
      <UtilisateurFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        roles={roles}
        totalUtilisateurs={totalCount}
      />

      {/* Liste des utilisateurs */}
      <UtilisateursList
        utilisateurs={users}
        loading={loading}
        selectedUtilisateurs={selectedUsers}
        onSelectUtilisateur={handleSelectUser}
        onSelectAll={handleSelectAll}
        onOpenModal={handleOpenModal}
        onOpenDeleteModal={handleOpenDeleteModal}
        onOpenPasswordModal={handleOpenPasswordModal}
        onNavigateToPermissions={handleNavigateToPermissions}
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
      />

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        maxWidth="max-w-4xl"
      >
        <UtilisateurForm
          formData={formData}
          formErrors={formErrors}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          submitting={submitting}
          selectedUtilisateur={selectedUser}
          onCancel={() => setIsModalOpen(false)}
          roles={roles}
        />
      </Modal>

      {/* Modal de changement de mot de passe */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title="Changer le mot de passe"
        maxWidth="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Entrez le nouveau mot de passe pour {selectedUserForPassword?.first_name} {selectedUserForPassword?.last_name}
          </p>
          <div className="space-y-4">
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654]"
              placeholder="Nouveau mot de passe"
            />
            <input
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654]"
              placeholder="Confirmer le nouveau mot de passe"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setIsPasswordModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={handlePasswordChange}
              className="px-4 py-2 bg-[#202654] text-white rounded-md text-sm font-medium hover:bg-[#1a1f45]"
            >
              Confirmer
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title="Supprimer l'utilisateur"
        message={`Êtes-vous sûr de vouloir supprimer l'utilisateur ${userToDelete?.first_name} ${userToDelete?.last_name} ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default UtilisateurTab;
